import { AssetGroup, type AssetDescription } from '@play-co/timestep-core';
import { ImageCoverView, View, animate } from '@play-co/timestep-core/ui';
import { user } from 'src/StateObserver';
import { dreamDriftTheme } from '../dreamDrift/dreamDrift.theme';
import {
  dreamDriftCostumeAssets,
  dreamDriftWearPet,
} from 'src/game/dreamDrift/dreamDrift.costumes';
import { runDreamDriftHeroPower } from 'src/game/dreamDrift/power/dreamDrift.power';
import { whirlwindDancerPowerAnimationAsset } from 'src/game/dreamDrift/power/dreamDrift.power.dancer.anim';
import { prismPainterPowerAnimationAssets } from 'src/game/dreamDrift/power/dreamDrift.power.painter.anim';
import { rumbleHeroPowerAnimationAsset } from 'src/game/dreamDrift/power/dreamDrift.power.rumble.anim';
import { skySummonerPowerAnimationAssets } from 'src/game/dreamDrift/power/dreamDrift.power.summoner.anim';
import { loadMatch2LevelAssets } from 'src/loadMatch2LevelAssets';
import i18n from 'src/lib/i18n/i18n';
import { captureGenericError } from 'src/lib/sentry';
import type { DreamDriftHero } from 'src/replicant/dreamDrift/dreamDrift.ruleset';
import type { GameScene } from '../minigame/match2-timestep/game/GameScene';
import type { GameEvent } from '../minigame/match2-timestep/game/defs/event';
import type { PuzzleStartData } from '../minigame/match2-timestep/match2.analytics';
import {
  trackDreamDriftPuzzleFinish,
  trackDreamDriftPuzzleStart,
} from '../dreamDrift/dreamDrift.analytics';
import Match2Scene from './Match2Scene';

/** Z-index used to keep the opening power input blocker above puzzle controls. */
const HERO_POWER_BLOCKER_Z_INDEX = 99999;

/** Resting Y position of the static pet in the Dream Drift puzzle scene. */
const DREAM_DRIFT_PET_Y = 225;

/** Presentation assets and sound effects loaded for each Dream Drift hero. */
const DREAM_DRIFT_HERO_POWER_ANIMATION_ASSETS: Record<
  DreamDriftHero,
  (string | AssetDescription<unknown>)[]
> = {
  rumbleHero: [
    rumbleHeroPowerAnimationAsset,
    'assets/sounds/sfx/dreamDrift/lightning.mp3',
  ],
  whirlwindDancer: [
    whirlwindDancerPowerAnimationAsset,
    'assets/sounds/sfx/dreamDrift/tornado.mp3',
  ],
  skySummoner: [
    ...skySummonerPowerAnimationAssets,
    'assets/sounds/sfx/dreamDrift/portal.mp3',
  ],
  prismPainter: [
    ...prismPainterPowerAnimationAssets,
    'assets/sounds/sfx/dreamDrift/paintdrop.mp3',
  ],
};

/** Isolated standard-rules puzzle scene for Dream Drift levels. */
export default class DreamDriftScene extends Match2Scene {
  /** Hero whose opening power should run for the configured puzzle. */
  public hero!: DreamDriftHero;

  /** Full-screen view blocking puzzle interaction during the opening power. */
  private interactionBlocker?: View;

  /** Promise resolved after the opening hero power and its cleanup complete. */
  private heroPowerReady?: Promise<void>;

  /** Analytics data shared by the current puzzle start and finish events. */
  private puzzleStartData?: PuzzleStartData;

  /** Create the Dream Drift puzzle with its feature-specific background. */
  constructor() {
    super({
      backgroundImage: dreamDriftTheme().getPuzzleBackground(),
      enableLoseRecovery: false,
      quitWarningMessage: i18n('dreamDrift.quit.message'),
    });
  }

  /** Show the standard puzzle runtime without its star score progress bar. */
  public override showScene() {
    this.puzzleStartData = undefined;

    // Block the board and every puzzle control before starting the scene.
    const interactionBlocker = new View({
      superview: this.getView(),
      infinite: true,
      zIndex: HERO_POWER_BLOCKER_Z_INDEX,
    });
    this.interactionBlocker = interactionBlocker;

    // Start the regular isolated puzzle scene.
    super.showScene();

    // Replace the puzzle map label with the active Dream Drift node.
    const node = user().dreamDrift?.instance?.playing ?? 0;
    if (node > 0) this.moves.setLevel(node);

    // Keep the static pet hidden until its opening power finishes.
    this.pet.updateOpts({
      y: DREAM_DRIFT_PET_Y,
      visible: false,
    });

    // Apply the selected costume after the base scene refreshes the pet.
    dreamDriftWearPet(this.pet, this.hero);

    // Hide only the score bar, preserving stars and the board's victory animation.
    this.game?.scoreSystem.progressBar?.hide();

    // Run the selected hero power once the initial board has settled.
    const game = this.game;
    if (!game) {
      interactionBlocker.removeFromSuperview();
      this.interactionBlocker = undefined;
      return;
    }

    // Mark this specialized runtime before any shared puzzle analytics can fire.
    game.isDreamDrift = true;
    game.events.subscribe(this.handleDreamDriftGameEvents.bind(this));
    this.heroPowerReady = this.playOpeningHeroPower(
      game,
      this.hero,
      interactionBlocker,
    );
  }

  /** Wait for the opening hero power before showing normal Match-2 tips. */
  protected override async triggerTips(): Promise<void> {
    // Keep startup tips behind the opening power lifecycle.
    await this.heroPowerReady;

    // Continue with the standard Match-2 tip sequence.
    await super.triggerTips();
  }

  /** Remove Dream Drift interaction state before destroying the puzzle runtime. */
  public override hideScene(): void {
    // Remove any remaining blocker from an interrupted opening sequence.
    this.interactionBlocker?.removeFromSuperview();
    this.interactionBlocker = undefined;
    this.heroPowerReady = undefined;
    this.puzzleStartData = undefined;

    // Destroy the standard Match-2 scene state.
    super.hideScene();
  }

  /** Load the selected Dream Drift level and feature background. */
  public override async preload() {
    // Refresh cached puzzle scenes when a later event selects a different theme.
    const backgroundImage = dreamDriftTheme().getPuzzleBackground();
    const view = this.getView();
    if (view instanceof ImageCoverView) view.setImage(backgroundImage);

    // Preload only the selected hero's presentation alongside shared assets.
    const heroPowerAssets =
      DREAM_DRIFT_HERO_POWER_ANIMATION_ASSETS[this.hero] ?? [];

    // Load the selected puzzle assets and Dream Drift presentation together.
    await Promise.all([
      loadMatch2LevelAssets(this.levelNumber),
      new AssetGroup([
        backgroundImage,
        'assets/sounds/music/DreamDrift_puzzle_BGM.mp3',
        ...dreamDriftCostumeAssets,
        ...heroPowerAssets,
      ]).load(),
    ]);
  }

  /**
   * Execute and clean up one Dream Drift opening hero power.
   *
   * @param game Match-2 runtime created for the current Dream Drift puzzle.
   * @param hero Hero selected for the current Dream Drift segment.
   * @param interactionBlocker View preventing input during the opening sequence.
   */
  private async playOpeningHeroPower(
    game: GameScene,
    hero: DreamDriftHero,
    interactionBlocker: View,
  ): Promise<void> {
    try {
      // Wait until the Match-2 runtime is initialized before observing its phase.
      await this.startGameReady;

      // Stop if this cached scene has already moved on to another game runtime.
      if (this.game !== game) return;

      // Track the attempt after the shared Match-2 runtime has initialized.
      this.puzzleStartData = trackDreamDriftPuzzleStart(game);

      // Run the hero handler after the initial board settles.
      await runDreamDriftHeroPower(game, hero);

      // Bring the static pet into the scene after its opening power finishes.
      await this.animatePetIntoScene();
    } catch (error) {
      // Report the failure while still releasing input to avoid a puzzle soft-lock.
      captureGenericError('Dream Drift hero power failed', error);
    } finally {
      // Release the exact blocker created for this opening sequence.
      interactionBlocker.removeFromSuperview();
      if (this.interactionBlocker === interactionBlocker) {
        this.interactionBlocker = undefined;
      }
    }
  }

  /** Track completion of the active Dream Drift puzzle attempt. */
  private handleDreamDriftGameEvents(event?: GameEvent): void {
    if (event?.id !== 'finish' || !this.game || !this.puzzleStartData) return;

    // Clear the snapshot before sending to prevent duplicate finish events.
    const puzzleStartData = this.puzzleStartData;
    this.puzzleStartData = undefined;

    // Delegate feature-specific finish composition to Dream Drift analytics.
    trackDreamDriftPuzzleFinish(this.game, puzzleStartData);
  }

  /**
   * Animate the static scene pet from above the screen to its resting pose.
   *
   * @returns Nothing after the pet entrance finishes.
   */
  private async animatePetIntoScene(): Promise<void> {
    // Reveal the static pet above the visible scene after its power finishes.
    this.pet.updateOpts({
      y: -800,
      scaleY: 4,
      visible: true,
    });

    // Drop the static pet into its regular Dream Drift position.
    await animate(this.pet)
      .now({ y: DREAM_DRIFT_PET_Y, scaleY: 1 }, 300, animate.easeOut)
      .toPromise();
  }
}
